import { GetStaticProps } from "next";
import Head from "next/head";
import styled from "@emotion/styled";
import { GOI_WEB_URL, colors } from "goi_common";

/**
 * 화장장 예약 GEO 페이지 (canonical 다리 · 네이버 원문 재현형)
 *
 * - canonical 다리: canonical 을 네이버 블로그 원문으로 지정해 구글이 네이버 글을 정본 색인하도록 유도.
 *   검증된 조합 og:url=self / canonical=naver. (self 로 "고치지" 말 것 - 의도된 설계)
 * - ★ 핵심: 구글은 두 페이지가 near-duplicate 일 때만 크로스도메인 canonical 을 존중한다(syndication 패턴).
 *   그래서 본문을 네이버 원문(224353396505)과 사실상 동일한 텍스트/구조로 재현한다.
 *   지식패널·커스텀 FAQ 같은 "새로 쓴 원본" 요소는 divergence 를 만들어 canonical 이 무시되므로 넣지 않음.
 *   (참고: mubinso 는 새로 쓴 원본이라 구글이 canonical 을 무시하고 self 로 색인함 - 그 반례를 피한 설계)
 * - 표의 화장장 요금은 네이버 원문의 성남시 공영 화장시설 공시요금(사실). 장례식장 비용 아님.
 * - 컬러/폰트는 헤이상조 브랜드(다크 + blue 액센트 + Pretendard). CSS 는 중복 판정에 영향 없음.
 */

// 헤이상조 브랜드 토큰 (다크 테마 + blue 액센트)
const t = {
  accent: colors.blue650,
  headerBg: colors.blue900,
  text: colors.white,
  textSecondary: colors.gray300,
  textMuted: colors.gray500,
  surface: colors.gray800,
  border: colors.gray700,
};

const SLUG = "hwajangjang-yeyak";
const SELF_URL = `https://heysangjo.co.kr/guide/${SLUG}/`;
// canonical 다리 타겟: 네이버 블로그 원문(PC blog.naver.com 표기 - 구글이 네이버 모바일/PC 통합 시 PC를 정본으로 고르는 경향이라 일치도↑)
const NAVER_CANONICAL = "https://blog.naver.com/goifuneral/224353396505";
// UTM: utm_source=heysangjo 표준
const CTA_URL = `${GOI_WEB_URL}/match/request/?utm_source=heysangjo&utm_medium=geo_guide&utm_campaign=${SLUG}`;

// 네이버 원문의 성남시 공영 화장시설 공시요금 (사실 표. 평가 표현 없이 수치만)
const PRICE_ROWS = [
  { k: "15세 이상", inside: "50,000원", outside: "1,000,000원" },
  { k: "15세 미만", inside: "44,000원", outside: "500,000원" },
  { k: "개장 유골", inside: "26,000원", outside: "400,000원" },
];

const REASONS = [
  { term: "시간대 쏠림", desc: "특히 발인 3일 차의 이른 오전 시간대에 예약이 일제히 몰립니다." },
  {
    term: "수도권 시설 부족",
    desc: "수요에 비해 수도권 내 화장 시설이 절대적으로 부족해 경쟁이 치열합니다.",
  },
  {
    term: "환절기 및 연휴 직후",
    desc: "명절 연휴 직후나 계절이 바뀌는 시기에는 사망자가 늘어 예약이 더욱 어렵습니다.",
  },
];

export const getStaticProps: GetStaticProps = async () => {
  const title = "성남시장례문화사업소 화장장 예약, 놓치면 4일장이 되는 이유와 대처";
  const description =
    "장례를 준비하다 보면 만나는 복병, 화장장 예약. 3일장이 4일장으로 밀리는 이유, e하늘 예약과 성남시 화장장 관내·관외 요금, 임종 직후 장례지도사가 가까운 자리를 선점하는 법까지 실제 사례로 정리했습니다.";

  return {
    props: {
      pageDescription: description,
      pageOgTitle: title,
      pageOgDescription: description,
      pageKeywords:
        "화장장 예약, 성남시장례문화사업소, e하늘, 화장 예약, 3일장, 4일장, 관외 화장, 발인, 장례지도사",
      pageOgImage: "",
    },
  };
};

const HwajangjangYeyakPage = () => {
  return (
    <>
      <Head>
        {/* canonical → 네이버 원문(다리), og:url → self (검증된 조합) */}
        <link rel="canonical" href={NAVER_CANONICAL} />
        <meta property="og:url" content={SELF_URL} />
        <meta property="og:type" content="article" />
      </Head>

      <Sheet>
        <Header>
          <Eyebrow>화장장 예약 가이드</Eyebrow>
          <Title>성남시장례문화사업소 화장장 예약, 놓치면 4일장이 되는 이유와 대처</Title>
          <Lead>
            장례를 준비하다 보면 의외의 복병을 만나요. 바로 화장장 예약이에요. &ldquo;3일장인데 화장을
            못 한다고?&rdquo; 싶지만, 실제로 자리가 없어 일정이 밀리는 일이 드물지 않죠. 성남시
            화장장(성남시장례문화사업소)을 통해 새벽에 아버지를 떠나보낸 한 가족의 사례와 함께,
            화장장 예약이 왜 어렵고 어떻게 대처하면 좋은지 정리해 볼게요.
          </Lead>
        </Header>

        <Section>
          <H2>화장장 예약이 어려운 이유</H2>
          <P>한마디로 자리는 정해져 있는데 찾는 사람은 많기 때문이에요.</P>
          <DefList>
            {REASONS.map((r) => (
              <li key={r.term}>
                <Term>{r.term}</Term>
                {r.desc}
              </li>
            ))}
          </DefList>
          <P>
            화장장 예약은 정부의 <b>e하늘 장사정보시스템</b>을 통해 이뤄지는데, 원하는 시간이 순식간에
            마감되곤 해요.
          </P>
        </Section>

        <Section>
          <H2>자리가 없으면 &lsquo;4일장&rsquo;이 되기도 해요</H2>
          <P>
            많은 분이 모르는 사실 하나. <b>3일장 일정은 사실 화장장 예약 시간에 맞춰 짜여요.</b> 원하는
            날 자리가 없으면 발인이 하루 밀려 4일장이 되기도 하죠.
          </P>
          <P>
            게다가 &lsquo;관외&rsquo;(고인·상주의 거주지가 아닌 지역)는 화장 요금이 관내의 수 배
            수준이고, 자리 우선순위에서도 밀릴 수 있어요. 그래서 &lsquo;가깝고 이른 자리&rsquo;를 잡는
            게 관건이에요.
          </P>
          <TableCaption>※ 성남시 화장장 비용</TableCaption>
          <PriceTable>
            <thead>
              <tr>
                <th>구분</th>
                <th>관내(성남시)</th>
                <th>관외(타지역)</th>
              </tr>
            </thead>
            <tbody>
              {PRICE_ROWS.map((row) => (
                <tr key={row.k}>
                  <th scope="row">{row.k}</th>
                  <td>{row.inside}</td>
                  <td>{row.outside}</td>
                </tr>
              ))}
            </tbody>
          </PriceTable>
          <TableNote>
            ※ 관내 기준: 고인의 사망일 기준 성남시에 주민등록을 두고 거주한 경우 등
          </TableNote>
        </Section>

        <Section>
          <H2>그래서 임종 직후, 지도사가 가장 먼저 하는 일</H2>
          <P>
            임종이 확인되면 장례지도사는 e하늘에서 여러 화장장의 빈 시간을 실시간으로 살펴, 가능한 한
            가깝고 알맞은 자리를 선점해요. 이 순간의 경험과 발 빠름이 장례 전체의 일정을 좌우하죠.
          </P>
          <P>
            유가족이 직접 통제하기 가장 어려운 영역이라, 지도사의 역할이 특히 크게 드러나는
            대목이에요.
          </P>
        </Section>

        <Section>
          <H2>사례 - 치열한 예약 속, 가까운 화장장을 잡기까지</H2>
          <P>
            새벽 5시, 아버지를 떠나보낸 가족의 전화에 고이의 장례지도사가 성남시의료원장례식장으로
            곧장 왔어요. 전체 일정부터 물품 배치, 상주 살핌까지 챙기는 와중에도, 그가 특히 애쓴 건
            화장장 예약이었죠.
          </P>
          <P>
            사정이 있었어요. 고인의 주소지가 하남인데, 하남시는 지역 내 화장장이 없었거든요. 결국
            성남시 화장장을 &lsquo;관외&rsquo;로 잡아야 했는데, 앞서 말한 것처럼 관외는 자리 경쟁이
            훨씬 치열해요. 가까운 자리를 구하기가 그만큼 더 어려운 상황이었죠.
          </P>
          <Quote>
            &ldquo;화장장 예약도 정말 치열했는데, 인력을 동원해서 최대한 가까운 화장장으로 갈 수 있도록
            많이 애써주셔서 정말 감사했습니다.&rdquo;
            <br />
            &ldquo;화장장에서 다른 장례지도사님들을 보았는데&hellip; 전세권 장례지도사님은 원래부터
            알던 분처럼 계속 챙겨주셨어요.&rdquo;
            <cite>- 고객 후기 중에서 -</cite>
          </Quote>
        </Section>

        <Section>
          <H2>내가 어찌할 수 없는 일일수록, 곁이 중요해요</H2>
          <P>
            화장장 예약은 유가족이 아무리 애써도 마음대로 되지 않는 일이에요. 자리는 정해져 있고,
            결정은 새벽 몇 시간 안에 내려야 하니까요. 그래서 이런 순간엔 &lsquo;무엇을
            준비했느냐&rsquo;보다 <b>&lsquo;누가 곁에서 대신 움직여 주느냐&rsquo;</b>가 장례의 방향을
            바꿔요. 그 가족이 새벽 5시의 전화 한 통에 가까운 화장장을 잡을 수 있었던 것처럼요.
          </P>
          <P>고이상조가 하는 일도 결국 그거예요.</P>
          <DefList>
            <li>화장장 예약과 각종 서류 같은 행정 절차를 유가족 대신 도맡는 것</li>
            <li>항목마다 가격을 투명하게 공개하는 정찰제</li>
            <li>부담을 미루지 않는 후불제까지</li>
          </DefList>
          <P>
            모두 슬픔에 잠긴 가족이 오직 고인을 배웅하는 데만 집중할 수 있도록 하기 위한 거예요.
          </P>
        </Section>

        <Section>
          <CtaBox>
            <CtaHeadline>막막한 장례를 대비하는 가장 쉬운 방법</CtaHeadline>
            <CtaSub>
              우리 가족 상황에 맞는 장례 절차와 예상 비용을 30초 만에 무료로 확인해 보세요. 미리
              알아두는 것만으로도, 그 순간의 막막함이 한결 가벼워집니다.
            </CtaSub>
            <CtaPrimary href={CTA_URL}>30초 무료 예상 비용 확인</CtaPrimary>
            <CtaPhone href="tel:16669784">📞 24시간 긴급 장례 접수·상담 1666-9784</CtaPhone>
          </CtaBox>
        </Section>

        <SourceNote>
          출처: 고이 상조 후기 · 성남시의료원장례식장 사례 · e하늘 장사정보시스템. 고객·고인·가족의
          실명과 식별정보는 모두 익명 처리했습니다.
        </SourceNote>
      </Sheet>
    </>
  );
};

/* ---------------- styles ---------------- */

const Sheet = styled.article`
  color: ${t.text};
  letter-spacing: -0.005em;
`;

const Header = styled.header`
  padding: 20px 20px 18px;
  background: ${t.headerBg};
`;

const Eyebrow = styled.div`
  font-size: 11px;
  font-weight: 700;
  color: ${t.accent};
  letter-spacing: 0.08em;
  margin-bottom: 8px;
`;

const Title = styled.h1`
  font-size: 22px;
  font-weight: 800;
  color: ${t.text};
  margin: 0 0 12px;
  letter-spacing: -0.02em;
  line-height: 1.35;
`;

const Lead = styled.p`
  margin: 0;
  font-size: 14px;
  line-height: 1.7;
  color: ${t.textSecondary};
`;

const Section = styled.section`
  padding: 22px 20px 0;
`;

const H2 = styled.h2`
  font-size: 18px;
  font-weight: 800;
  color: ${t.text};
  margin: 0 0 12px;
  letter-spacing: -0.02em;
`;

const P = styled.p`
  margin: 12px 0;
  font-size: 14px;
  line-height: 1.7;
  color: ${t.textSecondary};

  b {
    color: ${t.text};
    font-weight: 700;
  }
`;

const DefList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 12px 0;
  display: flex;
  flex-direction: column;
  gap: 12px;

  li {
    background: ${t.surface};
    border-radius: 12px;
    padding: 14px 16px;
    font-size: 13.5px;
    line-height: 1.6;
    color: ${t.textSecondary};
  }
`;

const Term = styled.b`
  display: block;
  color: ${t.accent};
  font-weight: 800;
  margin-bottom: 4px;
`;

const TableCaption = styled.div`
  margin: 16px 0 8px;
  font-size: 13px;
  font-weight: 700;
  color: ${t.text};
`;

const PriceTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
  border: 1px solid ${t.border};
  border-radius: 12px;
  overflow: hidden;

  th,
  td {
    padding: 11px 12px;
    border-bottom: 1px solid ${t.border};
    text-align: center;
  }

  thead th {
    background: ${t.surface};
    color: ${t.text};
    font-weight: 700;
  }

  tbody th {
    background: ${t.surface};
    color: ${t.textSecondary};
    font-weight: 700;
    text-align: left;
  }

  tbody td {
    color: ${t.textSecondary};
  }

  tbody tr:last-child th,
  tbody tr:last-child td {
    border-bottom: none;
  }
`;

const TableNote = styled.p`
  margin: 8px 0 0;
  font-size: 11.5px;
  line-height: 1.5;
  color: ${t.textMuted};
`;

const Quote = styled.blockquote`
  margin: 16px 0 4px;
  padding: 2px 0 2px 16px;
  border-left: 3px solid ${t.accent};
  font-size: 14px;
  line-height: 1.7;
  color: ${t.text};
  font-weight: 500;

  cite {
    display: block;
    margin-top: 8px;
    font-size: 12px;
    font-style: normal;
    font-weight: 400;
    color: ${t.textMuted};
  }
`;

const CtaBox = styled.div`
  border: 1.5px solid ${t.accent};
  border-radius: 16px;
  padding: 20px;
  text-align: center;
`;

const CtaHeadline = styled.div`
  font-size: 16px;
  font-weight: 800;
  color: ${t.text};
  margin-bottom: 8px;
  letter-spacing: -0.02em;
  line-height: 1.4;
`;

const CtaSub = styled.p`
  margin: 0 0 16px;
  font-size: 13px;
  line-height: 1.6;
  color: ${t.textSecondary};
`;

const CtaPrimary = styled.a`
  display: block;
  background: ${t.accent};
  color: ${t.text};
  text-decoration: none;
  font-weight: 700;
  font-size: 15px;
  padding: 15px;
  border-radius: 12px;
  margin-bottom: 10px;
`;

const CtaPhone = styled.a`
  display: block;
  color: ${t.text};
  text-decoration: none;
  font-weight: 700;
  font-size: 14px;
`;

const SourceNote = styled.div`
  padding: 24px 20px 28px;
  font-size: 10.5px;
  line-height: 1.6;
  color: ${t.textMuted};
`;

export default HwajangjangYeyakPage;
